import { useState, useCallback } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { useLocale } from '../../context/LocaleContext';
import { validateStep, isStepRequired } from '../../utilities/stepValidation';
import { logError } from '../../utilities/logging';

import styles from './personalisation-footer.module.css';

/**
 * Custom hook for PersonalisationFooter logic and state management.
 * @param {Object} props - Component props.
 * @returns {Object} View model for the footer component.
 */
export function useViewModel(props) {
  const {
    currentTotalPrice,
    updateTheCart,
    personalisationSteps,
    personalisationSlots,
    selections,
    isAddingToCart,
    hasPersonalisationOptions,
    product
  } = usePersonalisationContext();
  const { formatPrice, country } = useLocale();
  const { currentStep, totalSteps, onPrev, onNext, onClose } = props;

  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState('success');
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  // Get current step for validation.
  const stepIds = Array.from(personalisationSteps?.keys() || []);
  const currentStepId = stepIds[`${currentStep}`];
  const currentStepData = personalisationSteps?.get(currentStepId);

  // Validate based on whether there are personalisation options.
  let isStepValid;
  let stepValidation;
  if (!hasPersonalisationOptions) {
    // If no personalisation options, check if the base product is available for sale.
    isStepValid = product?.available === true;
  } else {
    // If there are personalisation options, validate the current step.
    stepValidation = validateStep({
      step: currentStepData,
      personalisationSlots: personalisationSlots || new Map(),
      selections: selections?.slots || []
    });
    isStepValid = stepValidation.isValid;
  }

  // Determine if the current step is required using the utility function.
  const isStepRequiredValue = hasPersonalisationOptions && currentStepData
    ? isStepRequired({
      step: currentStepData,
      personalisationSlots: personalisationSlots || new Map()
    })
    : false;

  // Check if user has made any selections on the current step.
  let hasSelectionsOnCurrentStep = false;
  if (hasPersonalisationOptions && currentStepData && currentStepData.slots) {
    hasSelectionsOnCurrentStep = currentStepData.slots.some((slotId) => {
      const slot = personalisationSlots?.get(slotId);
      const selection = selections?.slots?.find((selectionItem) => selectionItem.id === slotId);

      if (!slot || !selection) return false;

      // Check if there's a valid selection for this slot.
      return Boolean(selection.selected && selection.selected.toString().trim() !== '');
    });
  }

  // Only toastClassName has logic.
  const toastClassName = `${styles[`personalisation__footer-toast`]} ${styles[`personalisation__footer-toast--${toastType}`]}`;

  // Handlers
  const handleShareDesign = useCallback(() => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setToastType('success');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
        })
        .catch((err) => {
          setToastType('error');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
          logError({
            label: 'useViewModel',
            error: err,
            message: 'Failed to copy URL to clipboard',
            extraData: { url: window.location.href }
          });
        });
    }
  }, []);

  const handleSummaryModalOpen = useCallback(() => {
    setSummaryModalOpen(true);
  }, []);

  const handleSummaryModalClose = useCallback(() => {
    setSummaryModalOpen(false);
  }, []);

  const handlePrev = useCallback(() => {
    if (onPrev) onPrev();
  }, [onPrev]);

  const handleNext = useCallback(() => {
    if (onNext) onNext();
  }, [onNext]);

  const finishPersonalisation = useCallback(async () => {
    await updateTheCart({
      country,
      onSuccess: onClose
    });
  }, [updateTheCart, country, onClose]);

  const isLastStep = currentStep === totalSteps - 1;

  // Determine button text based on step requirements, selections, and slotType.
  const getButtonText = useCallback(() => {
    // Check if current step contains PAGE_CONTENT slotType.
    const isPageContentSlot = currentStepData?.slots?.some((slotId) => {
      const slot = personalisationSlots?.get(slotId);
      return slot?.slotType === 'PAGE_CONTENT';
    });

    if (isStepRequiredValue) {
      // Required steps always show standard text.
      return isLastStep ? 'Add to bag' : 'Continue';
    } else {
      // Non-required steps show different text based on selections.
      if (hasSelectionsOnCurrentStep) {
        // User has made selections, show standard text.
        return isLastStep ? 'Add to bag' : 'Continue';
      } else {
        // User hasn't made selections, show skip text.
        // Special case for PAGE_CONTENT slots.
        if (isPageContentSlot) {
          return isLastStep ? 'Add to bag' : 'Continue';
        }
        return isLastStep ? 'Skip & add to bag' : 'Skip & Continue';
      }
    }
  }, [isStepRequiredValue, isLastStep, hasSelectionsOnCurrentStep, currentStepData, personalisationSlots]);

  return {
    toastClassName,
    showToast,
    toastType,
    handleShareDesign,
    handleSummaryModalOpen,
    handleSummaryModalClose,
    summaryModalOpen,
    handlePrev,
    handleNext,
    finishPersonalisation,
    currentStep,
    totalSteps,
    currentTotalPrice,
    isLastStep,
    isStepValid,
    isStepRequired: isStepRequiredValue,
    hasSelectionsOnCurrentStep,
    isAddingToCart,
    formatPrice,
    getButtonText
  };
}
